import React from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle, Snowflake } from "lucide-react";
import type { CalcStatus } from "@/types/calc";

// ── Single source of truth for the app header ──────────────────────────────
// Both the Calculator and the Production Gantt pages render THIS component so
// the bar, nav and status chip stay pixel-identical and never jump when you
// switch pages. Page-specific bits (active tab, status, model code, extra
// meta) are passed in as props; everything structural lives here.

export type StatusTone = "ready" | "busy" | "ok" | "error";

export interface StatusBadge {
  tone: StatusTone;
  label: string;
}

interface HeaderProps {
  status: StatusBadge;
  modelCode?: string;
  /** Extra second-row chips (e.g. Gantt: ["16 h shift", "1 crew"]). */
  meta?: string[];
  activePage?: "calculator" | "gantt" | "scenarios" | "settings";
}

// Navigation is defined once — the only real routes in the app.
// "Saved Scenarios" hidden per request (no menu button / user interaction).
const NAV_ITEMS = [
  { key: "calculator", label: "Calculator", path: "/" },
  { key: "gantt", label: "Production Gantt", path: "/gantt" },
  { key: "settings", label: "Settings", path: "/settings" },
  // { key: "scenarios", label: "Saved Scenarios", path: "/scenarios" },
] as const;

const TONE: Record<StatusTone, { dot: string; icon: React.ReactNode }> = {
  ready: { dot: "bg-white/70", icon: <Snowflake className="h-3.5 w-3.5" /> },
  busy: { dot: "bg-[var(--js-bright-blue)] animate-pulse", icon: <Loader2 className="h-3.5 w-3.5 animate-spin" /> },
  ok: { dot: "bg-[var(--js-green)]", icon: <CheckCircle2 className="h-3.5 w-3.5" /> },
  error: { dot: "bg-[var(--js-red)]", icon: <AlertCircle className="h-3.5 w-3.5" /> },
};

/** Map the Calculator's CalcStatus onto the shared status badge. */
export function calcStatusToBadge(status: CalcStatus): StatusBadge {
  switch (status) {
    case "updating": return { tone: "busy", label: "Updating\u2026" };
    case "ok": return { tone: "ok", label: "Up to date" };
    case "invalid": return { tone: "error", label: "Invalid input" };
    case "error": return { tone: "error", label: "Offline" };
    case "idle":
    default: return { tone: "ready", label: "Ready" };
  }
}

function StatusChip({ status }: { status: StatusBadge }) {
  const s = TONE[status.tone];
  return (
    <div
      data-testid="calc-status-indicator"
      aria-live="polite"
      // Fixed min-width + right-justified content keeps the nav anchored so the
      // chip never shoves the menu around as its label changes.
      className="ml-auto md:ml-0 inline-flex min-w-[150px] items-center justify-end gap-2 rounded-md bg-[rgba(51,66,79,0.45)] px-3 py-1.5 text-sm font-semibold text-white"
    >
      <span className={`h-2 w-2 rounded-full ${s.dot}`} />
      {s.icon}
      <span>{status.label}</span>
    </div>
  );
}

export const Header: React.FC<HeaderProps> = ({
  status, modelCode, meta = [], activePage = "calculator",
}) => {
  const navigate = useNavigate();

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-50 bg-[var(--js-blue)] border-b border-white/15 shadow-sm"
    >
      <div className="mx-auto max-w-[1540px] px-4 sm:px-6">
        <div className="flex items-baseline gap-4 pt-[15px] pb-1.5">
          <img
            src="https://brand.jackstone.com/logo-white.png"
            alt="Jackstone"
            className="h-7 w-auto self-baseline"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
          <span className="hidden xl:block text-sm font-medium text-white/70">
            Frozen Today, Fresh Tomorrow
          </span>

          <nav className="ml-auto hidden md:flex items-baseline gap-1">
            {NAV_ITEMS.map((item) => {
              const active = item.key === activePage;
              return (
                <button
                  key={item.key}
                  data-testid={`nav-${item.key}`}
                  type="button"
                  aria-current={active ? "page" : undefined}
                  onClick={() => { if (!active) navigate(item.path); }}
                  className={
                    active
                      ? "rounded-md bg-white/15 px-3 py-1.5 text-sm font-semibold text-white cursor-default"
                      : "rounded-md px-3 py-1.5 text-sm font-semibold text-white/70 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                  }
                >
                  {item.label}
                </button>
              );
            })}
          </nav>

          <StatusChip status={status} />
        </div>

        {/* Second row — always rendered (min height) so it never shifts the
            layout vertically between pages. Holds the model code plus any
            page-specific meta chips. */}
        <div className="hidden min-h-[24px] items-center gap-2 pb-2 -mt-1 sm:flex">
          {modelCode && (
            <>
              <span className="text-sm font-medium text-white/65">Model code</span>
              <span
                data-testid="model-code"
                className="rounded bg-white/12 px-2 py-0.5 text-sm font-semibold text-white tabular-nums"
              >
                {modelCode}
              </span>
            </>
          )}
          {meta.map((m, i) => (
            <React.Fragment key={i}>
              <span className="text-white/35">&middot;</span>
              <span className="text-sm font-medium text-white/65">{m}</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    </header>
  );
};
